/**
 * Created by Jonas Wålekvist on 2018-05-31.
 */

import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    ISymbolStates,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Power2, TimelineLite, TweenLite} from "gsap";
import {DeadwoodGameConfig} from "../DeadwoodGameConfig";

require("@nolimitcity/slot-game/lib/gsap/src/umd/CustomWiggle.js");

export interface IDeadwoodSymbolStates extends ISymbolStates {
    noWin:ISymbolStateConfig;
    landing:ISymbolStateConfig;
    spinAttention:ISymbolStateConfig;
    broken:ISymbolStateConfig;
}

export class DeadwoodReelSymbol extends StateReelSymbol {

    private _effectContainer:PIXI.Container;
    private _bustedWildContainer:PIXI.Container;

    private _bustedWild:MultiSprites;
    private _wildShowSprite:PIXI.Sprite;
    private _wildShowSpine:PIXI.spine.Spine;
    private _bonusWinOverlaySpine:PIXI.spine.Spine;
    private _wildShowGlowSpine:PIXI.spine.Spine;

    private _bonusOverlay:PIXI.Sprite;
    private _bonusBurning:boolean = false;
    private _bonusNearWin:boolean = false;

    public isPlayingIdle:boolean = false;
    public idleTimeline:TimelineLite | null;

    public shouldPlayWinText:{
        position:[number, number],
        betWayWinData:IBetWayWinData
    };
    public winTextPlayed:boolean = false;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    protected initAnimations():void {
        this._effectContainer = new PIXI.Container();
        this.addChild(this._effectContainer);

        this._bustedWildContainer = new PIXI.Container();
        this.addChild(this._bustedWildContainer);
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        const result:boolean = super.initState(stateConfig);

        if(this._effectContainer) {
            this.addChild(this._effectContainer);
        }

        if(this._bustedWildContainer) {
            this.addChild(this._bustedWildContainer);
        }

        return result;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        if(this.isPlayingIdle) {
            if(this.idleTimeline && this.idleTimeline.isActive()) {
                this.idleTimeline.progress(1);
            }
            this.idleTimeline = null;
            this.isPlayingIdle = false;
        }

        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === DeadwoodGameConfig.instance.SYMBOL_STATES.win.keyword && !this.symName.includes("W")) {
            const winTl:TimelineLite = new TimelineLite();
            winTl.add(new TweenLite(this.scale, 0.5, {x : 1.15, y : 1.15}));
            winTl.add(() => new TweenLite(this.scale, 0.3, {x : 1, y : 1}));
            timeline.add(winTl, delay);
            return;
        }

        if(this._bustedWild && this._bustedWild.parent) {
            if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
                timeline.add(this._bustedWild.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            } else {
                timeline.add(this._bustedWild.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
        }
    }

    private createBonusOverlay():void {
        if(!this.symName.includes("L")) {
            debugger;
            Logger.logDev("DeadwoodReelSymbol.createBonusOverlay() can only play on low symbols!");
            return;
        }

        if(!this._bonusOverlay) {
            this._bonusOverlay = new PIXI.Sprite(GameResources.getTextures(`bustedWildFrame`)[0]);
            this._bonusOverlay.anchor.set(0.5);
            this._bonusOverlay.alpha = 0;
        }
    }

    public playBonusAttention(stay:boolean):void {
        this.createBonusOverlay();
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => this._effectContainer.addChild(this._bonusOverlay),
            new TweenLite(this._bonusOverlay, 0.3, {alpha : 1, ease : Power2.easeOut}),
            new TweenLite(this._bonusOverlay.scale, 0.3, {x : 1.25, y : 1.25})
        ]);
        timeline.add(() => {
            if(!stay && !this._bonusNearWin) {
                new TweenLite(this._bonusOverlay, 0.2, {alpha : 0, ease : Power2.easeIn});
            }
            new TweenLite(this._bonusOverlay.scale, 0.2, {x : 1, y : 1})
        });
    }

    public burnBonus(on:boolean, nearWin:boolean):void {
        this.createBonusOverlay();
        this._bonusBurning = on;
        this._bonusNearWin = nearWin;
        TweenLite.to(this._bonusOverlay, 0.3, {
            alpha : on ? 1 : 0,
            onComplete : () => {
                if(on && nearWin) {
                    this.burnBonusWiggle();
                }
            }
        });
    }

    private burnBonusWiggle():void {
        if(!this._bonusBurning) {
            return;
        }
        CustomWiggle.create("burnBonusWiggle", {wiggles : this._bonusNearWin ? 5 : 10, type : "uniform"});
        TweenLite.fromTo(this._bonusOverlay, 2,
            {alpha : 1},
            {alpha : 0.1, ease : "burnBonusWiggle", onComplete : () => this.burnBonusWiggle()}
        );
    }

    public showWild(animate:boolean):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        if(!this._bustedWild) {
            this._bustedWild = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("bustedWild"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("bustedWild"),
                    anchor : [0.5, 0.5]
                }
            });
        }

        if(animate) {
            if(!this._wildShowSprite && animate) {
                this._wildShowSprite = new PIXI.Sprite();
                this._wildShowSprite.anchor.set(0.5);
                this._wildShowSprite.alpha = 0;
                this._effectContainer.addChild(this._wildShowSprite);

                this._wildShowSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("bonusWildShow"));
                this._wildShowSpine.autoUpdate = true;
                this._wildShowSprite.addChild(this._wildShowSpine);

                this._bonusWinOverlaySpine = new PIXI.spine.Spine(GameResources.getSpineAsset(`${this.symName}_wildOverlay`));
                this._bonusWinOverlaySpine.autoUpdate = true;
                this._wildShowSprite.addChild(this._bonusWinOverlaySpine);

                this._wildShowGlowSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("bonusWildGlow"));
                this._wildShowGlowSpine.autoUpdate = true;
                this._wildShowSprite.addChild(this._wildShowGlowSpine);
            }
            timeline.add([
                () => {
                    this._wildShowSpine.state.setAnimation(0, "animation", false);
                    this._bonusWinOverlaySpine.state.setAnimation(0, "animation", false);
                    this._wildShowGlowSpine.state.setAnimation(0, "animation", false);
                },
                TweenLite.to(this._wildShowSprite, 0.1, {alpha : 1})
            ]);
        }

        timeline.add([
            this._bustedWild.playSprite("normal",
                {
                    duration : 0,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : 0,
                    vars : [{toVars : {alpha : 0}}]
                }),
            () => {
                this._bustedWildContainer.addChild(this._bustedWild);
            }
        ], animate ? "+=0.6" : 0);
        return timeline;
    }

    protected reset(remove:boolean):void {
        super.reset(remove);

        if(remove) {
            if(this._bustedWild) {
                this._bustedWildContainer.removeChild(this._bustedWild);
                this._bustedWild.destroy();
            }

            if(this._wildShowSprite) {
                if(this._wildShowSprite.parent) {
                    this._effectContainer.removeChild(this._wildShowSprite);
                }
                this._wildShowSprite.removeChild(this._wildShowSpine);
                this._wildShowSpine.destroy();
                this._wildShowSprite.removeChild(this._bonusWinOverlaySpine);
                this._bonusWinOverlaySpine.destroy();
                this._wildShowSprite.removeChild(this._wildShowGlowSpine);
                this._wildShowGlowSpine.destroy();
                this._wildShowSprite.destroy();
            }

            if(this._bonusOverlay) {
                if(this._bonusOverlay.parent) {
                    this._effectContainer.removeChild(this._bonusOverlay);
                }
                this._bonusOverlay.destroy();
            }

            if(this._effectContainer) {
                this._effectContainer.destroy();
            }
            if(this._bustedWildContainer) {
                this._bustedWildContainer.destroy();
            }
        }
    }
}